"use client";

import { createContext, useContext, useEffect, useRef, useState, type ReactNode } from "react";
import Link from "next/link";
import { CONSENT_VERSION, rejectedChoices, type CookieChoices } from "@/lib/privacy/choices";
import { retainCurrentRoomLink } from "@/lib/room-client";
import { Button } from "./ui/Button";

const Context = createContext<{ choices: CookieChoices | null; rememberedEmail: string;
  setRememberedEmail: (email: string) => void; openSettings: () => void } | null>(null);
export function useCookieConsent() {
  const context = useContext(Context);
  if (!context) throw new Error("Cookie consent provider is missing.");
  return context;
}
export function CookieSettingsButton({ children = "Cookie settings" }: { children?: ReactNode }) {
  const { openSettings } = useCookieConsent();
  return <button type="button" onClick={openSettings} className="underline underline-offset-4 hover:text-ink cursor-pointer">{children}</button>;
}

export function CookieConsentProvider({ children }: { children: ReactNode }) {
  const [choices, setChoices] = useState<CookieChoices | null>(null);
  const [loaded, setLoaded] = useState(false);
  const [rememberedEmail, setRememberedEmail] = useState("");
  const [draft, setDraft] = useState<CookieChoices>({ ...rejectedChoices });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const dialog = useRef<HTMLDialogElement>(null);
  const clearLegacyStorage = () => {
    try { localStorage.removeItem("iva_invite_code"); } catch { /* Storage may be disabled. */ }
  };
  useEffect(() => {
    const controller = new AbortController();
    const load = async () => {
      try {
        const response = await fetch("/api/privacy/consent", { cache: "no-store", signal: controller.signal });
        if (!response.ok) throw new Error("Could not load cookie preferences.");
        const data = await response.json();
        if (controller.signal.aborted) return;
        setChoices(data.consent);
        setRememberedEmail(data.rememberedEmail || "");
        if (!data.consent?.preferences) clearLegacyStorage();
      } catch { /* Unknown consent keeps optional features off. */ }
      finally { if (!controller.signal.aborted) setLoaded(true); }
    };
    void load();
    return () => controller.abort();
  }, []);
  const openSettings = () => {
    setDraft(choices || { ...rejectedChoices });
    setError("");
    dialog.current?.showModal();
  };
  const save = async (next: CookieChoices) => {
    if (saving) return;
    setSaving(true);
    setError("");
    if (!next.preferences) retainCurrentRoomLink();
    try {
      const response = await fetch("/api/privacy/consent", { method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ ...next, version: CONSENT_VERSION }) });
      const data = await response.json();
      if (!response.ok) throw new Error(data.error || "Could not save your choice. Please try again.");
      setChoices(data.consent);
      if (!next.preferences) { setRememberedEmail(""); clearLegacyStorage(); }
      dialog.current?.close();
    } catch (err) { setError(err instanceof Error ? err.message : "Could not save your choice."); }
    finally { setSaving(false); }
  };
  return <Context.Provider value={{ choices, rememberedEmail, setRememberedEmail, openSettings }}>
    {children}
    {loaded && choices === null && <section aria-label="Cookie choices" className="fixed bottom-0 inset-x-0 z-40 border-t border-line bg-paper p-4 sm:p-5 shadow-xl">
      <div className="max-w-6xl mx-auto flex flex-col lg:flex-row lg:items-center gap-4">
        <div className="flex-1 text-sm">
          <h2 className="font-semibold text-ink">Your cookie choices</h2>
          <p className="text-ink-soft mt-1">With your permission, we’ll remember your email, invite code and room access, and enable Awin / Partnerize referral tracking when you visit a retailer. Rejecting saves only your choice. <Link href="/privacy#cookies" className="underline underline-offset-2">Cookie details</Link></p>
        </div>
        <div className="flex flex-wrap items-center gap-2 shrink-0">
          <Button variant="outline" size="sm" disabled={saving} onClick={() => void save({ ...rejectedChoices })}>Reject all</Button>
          <Button variant="outline" size="sm" disabled={saving} onClick={() => void save({ preferences: true, affiliates: true })}>Accept all</Button>
          <button type="button" disabled={saving} onClick={openSettings} className="text-sm underline underline-offset-4 px-2 py-2">Choose cookies</button>
        </div>
      </div>
      {error && <p role="alert" className="max-w-6xl mx-auto text-sm text-red-700 mt-2">{error}</p>}
    </section>}
    <dialog ref={dialog} aria-labelledby="cookie-settings-title" onCancel={event => { if (saving) event.preventDefault(); }}
      className="m-auto w-[calc(100%-2rem)] max-w-lg max-h-[90dvh] overflow-y-auto rounded-2xl border border-line bg-paper p-6 text-ink shadow-xl backdrop:bg-ink/60">
      <h2 id="cookie-settings-title" className="text-2xl font-semibold">Cookie settings</h2>
      <p className="text-sm text-ink-soft mt-3">Optional cookies start off. Your choice is remembered for six months. You can change it here at any time.</p>
      <div className="my-5 space-y-5 text-sm">
        <p><strong>Consent choice — always stored after you choose.</strong> If you reject all, this is the only cookie we keep. Private room links still work.</p>
        <label className="flex items-start gap-3"><input type="checkbox" checked={draft.preferences} disabled={saving} onChange={event => setDraft(value => ({ ...value, preferences: event.target.checked }))} className="mt-1 accent-clay" />
          <span><strong>Remember my details</strong><span className="block text-ink-soft mt-1">Remember your email and beta invite code for 90 days, and private room access for up to six months. This does not sign you up for marketing emails.</span></span></label>
        <label className="flex items-start gap-3"><input type="checkbox" checked={draft.affiliates} disabled={saving} onChange={event => setDraft(value => ({ ...value, affiliates: event.target.checked }))} className="mt-1 accent-clay" />
          <span><strong>Affiliate referrals</strong><span className="block text-ink-soft mt-1">Allow Awin and Partnerize tracking when you follow an approved retailer link, so purchases can earn us a commission. Off means ordinary retailer links.</span></span></label>
        <p className="text-ink-soft">Rejecting removes our optional cookies and stops future affiliate redirects. Cookies already set on a retailer’s or affiliate network’s website are controlled by that website. <Link href="/privacy#cookies" className="underline">Read the cookie details</Link>.</p>
      </div>
      {error && <p role="alert" className="text-sm text-red-700 mb-3">{error}</p>}
      <div className="flex flex-wrap gap-2">
        <Button variant="outline" disabled={saving} onClick={() => void save({ ...rejectedChoices })}>Reject all</Button>
        <Button variant="outline" disabled={saving} onClick={() => void save({ preferences: true, affiliates: true })}>Accept all</Button>
        <Button disabled={saving} onClick={() => void save(draft)}>Save choices</Button>
        <button type="button" disabled={saving} onClick={() => dialog.current?.close()} className="p-2 text-sm underline">Close</button>
      </div>
    </dialog>
  </Context.Provider>;
}
